import React, { useEffect, useMemo, useState, useRef } from 'react';
import { IState, useAppSelector } from 'appRedux/createStore';
import { useDispatch, useSelector } from 'react-redux';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { FilterReduxCommandCreator } from 'appRedux/actions/filterCommandCreator';
import { playerTypeEnum } from 'common/commonEnum';
import { InsightValueEnum } from 'config/Insights';
import { InsightFilterEnum } from 'appRedux/models/favoritesModels';
import { config } from 'config/config';
import { InsightValues } from 'common/types';
import { FilterTypeEnum, OrderByEnum } from 'common/common';
import { ReduxCommandCreator } from 'appRedux/actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import { useHistory, useLocation } from 'react-router';

const RPT_HOTTEST_VALUE = 'return_desc';
const RPT_COLDEST_VALUE = 'return_asc';

/** Explore + Compare insight chip order (InsightInline is shared). Popular after Jackpots. */
export const insightOrderMap: Record<string, number> = {
  [RPT_HOTTEST_VALUE]: 1,
  [RPT_COLDEST_VALUE]: 2,
  [InsightValueEnum.jackpot_value]: 3,
  [InsightValueEnum.jackpots]: 3,
  [InsightValueEnum.spins]: 4, // Popular — directly after Jackpots
  [InsightValueEnum.money_won]: 5,
  [InsightValueEnum.money_played]: 6,
  [InsightValueEnum.avg_money_won_per_spin]: 7,
  [InsightValueEnum.jackpot_value_per_spin]: 8,
  [InsightValueEnum.non_jkpt_won_per_spin]: 9,
};

const compareLabelOverrides: Partial<Record<InsightValueEnum, string>> = {
  [InsightValueEnum.money_won]: '$ Won',
  [InsightValueEnum.money_played]: '$ Played',
  [InsightValueEnum.avg_money_won_per_spin]: 'W/S',
  [InsightValueEnum.jackpot_value_per_spin]: 'JW/S',
  [InsightValueEnum.non_jkpt_won_per_spin]: 'W/S - J',
  [InsightValueEnum.spins]: 'Popular',
};

const getDisplayInsightValue = (value: string | undefined, isSlotOrderAscending: boolean) => {
  if (value === InsightValueEnum.return) {
    return isSlotOrderAscending ? RPT_HOTTEST_VALUE : RPT_COLDEST_VALUE;
  }
  return value;
};

type InsightOption = {
  label: string;
  value: string;
  color?: string;
  description?: string;
  tooltipInfo?: string;
  backgroundColor?: string;
  icon?: string;
  asset_type?: string;
  icon_text?: string;
  playerTypeAccess?: playerTypeEnum[];
  enableGraph?: boolean;
  enableMainPullDown?: boolean;
  orderGraph?: number;
  display_order?: number;
  compareLabel?: string;
  locked: boolean;
  baseValue: string;
  sortDirection?: 'desc' | 'asc';
};

interface InsightInlineProps {
  selectedInsightValue?: string;
  onSelect?: () => void;
}

const InsightInline: React.FC<InsightInlineProps> = ({ selectedInsightValue, onSelect }) => {
  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);
  const filterCommandCreator = FilterReduxCommandCreator(dispatch);
  const commands = ReduxCommandCreator(dispatch);
  const { selectedInsight } = useAppSelector((state) => state.app.persistedState.compareSlotsState);
  const history = useHistory();
  const location = useLocation();

  const isProUser = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isProUser
  ) as boolean;
  const isSlotOrderAscending = useSelector<IState>(
    (state) => state.app.persistedState.isSlotOrderAscending
  ) as boolean;

  const playerType = isProUser ? playerTypeEnum.slotCheckPro : playerTypeEnum.slotCheck;

  const allInsights = useMemo(() => {
    const insightRestrictions: InsightValues = [
      InsightValueEnum.jackpots,
      InsightValueEnum.win_rate,
    ];
    const combinedInsights = [...(config.slotInsights || [])].sort(
      (a, b) => Number(a.display_order) - Number(b.display_order)
    );

    const newOptions: InsightOption[] = [];

    combinedInsights.forEach((slotInsight) => {
      const insightData = config.hotSlotsLabels[slotInsight.value];
      const isAllowedPlayer = slotInsight.playerTypeAccess?.includes(playerType);
      const isRestricted = insightRestrictions?.some?.(
        (restriction) => slotInsight.value === restriction
      );
      // Include all insights that belong in the main pulldown, regardless of access
      if (slotInsight.enableMainPullDown && !isRestricted) {
        const commonOption = {
          label: insightData?.label ?? slotInsight.label,
          value: slotInsight.value,
          color: insightData?.color,
          description: slotInsight.short_name ?? insightData?.label,
          tooltipInfo: slotInsight.description,
          backgroundColor: slotInsight.backgroundColor,
          icon: insightData?.icon,
          asset_type: insightData?.asset_type,
          icon_text: insightData?.text,
          playerTypeAccess: slotInsight.playerTypeAccess,
          enableGraph: slotInsight.enableGraphPulldown,
          enableMainPullDown: slotInsight.enableMainPullDown,
          orderGraph: slotInsight.graphPulldownOrder,
          display_order: slotInsight.display_order,
          compareLabel:
            compareLabelOverrides[slotInsight.value as InsightValueEnum] ?? slotInsight.compareLabel,
          locked: !isAllowedPlayer,
          baseValue: slotInsight.value,
        } as InsightOption;

        if (slotInsight.value === InsightValueEnum.return) {
          newOptions.push({
            ...commonOption,
            value: RPT_HOTTEST_VALUE,
            compareLabel: 'Hottest',
            sortDirection: 'desc',
          });
          newOptions.push({
            ...commonOption,
            value: RPT_COLDEST_VALUE,
            compareLabel: 'Coldest',
            sortDirection: 'asc',
          });
        } else {
          newOptions.push(commonOption);
        }
      }
    });

    return newOptions.sort((a, b) => {
      const aRank = insightOrderMap[a.value] ?? insightOrderMap[a.baseValue] ?? 999;
      const bRank = insightOrderMap[b.value] ?? insightOrderMap[b.baseValue] ?? 999;
      if (aRank !== bRank) return aRank - bRank;
      return (a.display_order ?? 0) - (b.display_order ?? 0);
    });
  }, [playerType]);

  const [tempInsight, setTempInsight] = useState<string | undefined>(
    getDisplayInsightValue(selectedInsightValue ?? selectedInsight, isSlotOrderAscending)
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !tempInsight) return;

    let retryCount = 0;
    const maxRetries = 5;

    const scrollToActivePill = () => {
      const activePill = container.querySelector('.insight-chip.filled') as HTMLElement;
      if (activePill) {
        const offsetLeft = activePill.offsetLeft;
        const chipWidth = activePill.offsetWidth;
        const containerScrollLeft = container.scrollLeft;
        const containerWidth = container.offsetWidth;

        if (offsetLeft < containerScrollLeft || offsetLeft + chipWidth > containerScrollLeft + containerWidth) {
          container.scrollTo({
            left: offsetLeft - 10,
            behavior: 'smooth',
          });
        }
        return;
      }

      if (retryCount < maxRetries) {
        retryCount++;
        requestAnimationFrame(scrollToActivePill);
      }
    };

    requestAnimationFrame(scrollToActivePill);
  }, [tempInsight]);

  useEffect(() => {
    setTempInsight(
      getDisplayInsightValue(selectedInsightValue ?? selectedInsight, isSlotOrderAscending)
    );
  }, [selectedInsightValue, selectedInsight, isSlotOrderAscending]);

  const handleSave = (selectedItem?: InsightOption) => {
    const itemToSave = selectedItem ?? allInsights.find((item) => item.value === tempInsight);
    const valueToSave = itemToSave?.baseValue ?? tempInsight;
    const isRptSelection = valueToSave === InsightValueEnum.return;
    // Jackpot insights are only meaningful sorted high-to-low, so always force
    // descending order regardless of the sort direction in effect beforehand.
    const isJackpotSelection =
      valueToSave === InsightValueEnum.jackpots ||
      valueToSave === InsightValueEnum.jackpot_value;
    let selectedOrderBy = isSlotOrderAscending ? OrderByEnum.Desc : OrderByEnum.Asc;
    if (itemToSave?.sortDirection) {
      selectedOrderBy = itemToSave.sortDirection === 'desc' ? OrderByEnum.Desc : OrderByEnum.Asc;
    }
    if (isJackpotSelection) {
      selectedOrderBy = OrderByEnum.Desc;
    }

    if (isRptSelection && itemToSave?.sortDirection) {
      commands.SetSlotOrderState(itemToSave.sortDirection === 'desc');
    }

    // The hot list derives its top/bottom (descending/ascending) ordering from
    // isSlotOrderAscending, so persist it too — otherwise the filter would be
    // descending while the list still rendered the previous direction.
    if (isJackpotSelection) {
      commands.SetSlotOrderState(true);
    }

    if (valueToSave && valueToSave !== 'none') {
      slotCompareCommands.setSortBy({ compareSortBy: valueToSave });
      filterCommandCreator.SetFilterOptions(FilterTypeEnum.hottestSlots, {
        insight: valueToSave as InsightValueEnum,
        orderby: selectedOrderBy,
      });
    } else {
      slotCompareCommands.setSortBy({ compareSortBy: InsightFilterEnum.ReturnToPlayer });
    }

    valueToSave && slotCompareCommands.setSelectedInsight({ insight: valueToSave });
  };

  if (!allInsights || allInsights.length === 0) return null;

  return (
    <div className="settings-section">
      {/* <div className="settings-title-row">
        <IonText className="section-title" style={{ marginTop: '16px' }}>INSIGHT TYPE</IonText>
      </div> */}

      <div ref={scrollContainerRef} className="button-group button-group--scroll-x button-group--insight">
        {allInsights.map((item) => (
          <button
            key={item.value}
            className={`insight-chip ${tempInsight === item.value ? 'filled' : 'outline'}${item.locked ? ' locked' : ''}`}
            onClick={() => {
              if (item.locked) {
                history.push('/auth/stripe', { previousPage: location.pathname });
                return;
              }
              setTempInsight(item.value);
              handleSave(item);
              onSelect?.();
            }}
          >
            <span className="button-text">{item.compareLabel ?? item.description ?? item.label}</span>
            {item.locked && <FontAwesomeIcon icon={faLock} className="icon-lock" />}
          </button>
        ))}
      </div>
    </div>
  );
};

export default InsightInline;
